import React, { useState, useCallback, useRef, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import findIndex from "lodash/findIndex";
import { Helmet } from "react-helmet";
import {
  Button,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  useTheme,
  styled,
} from "@material-ui/core";
import { compose, spacing, display } from "@material-ui/system";
import { Skeleton } from "@material-ui/lab";
import { CustomDialog } from "../../../components/CustomDialog";
import { CaseIcon, CloseIcon } from "../../../icons";
import { AccountContact } from "../../../api/services";
import { ContactPanel } from "./ContactPanel";
import { ContactSelector } from "./ContactSelector";
import { ErrorFallback } from "../../../components/Errors";
import { FormContext, useForm } from "react-hook-form";

const CustomDialogContent = styled(DialogContent)(compose(spacing, display));

const AccountNumberLine: React.FC<{
  accountNumber: string;
}> = ({ accountNumber }) => (
  <Box display="flex" alignItems="center" color="text">
    <Box clone mt="-2px" mr={1.5}>
      <CaseIcon fill="currentColor" />
    </Box>
    <Typography variant="h6">{accountNumber}</Typography>
  </Box>
);

type Props = {
  accountNumber: string;
  contacts: AccountContact[] | null;
  error: Error | null;
  onCancel: () => void;
  onSkip: () => void;
  onConfirm: () => void;
};

type TitleProps = Pick<Props, "accountNumber" | "onCancel">;

const CallVerificationTitle: React.FC<TitleProps> = ({
  accountNumber,
  onCancel,
}) => {
  const theme = useTheme();
  return (
    <>
      <AccountNumberLine accountNumber={accountNumber} />
      <Box
        position="absolute"
        mt="-2px"
        right={theme.spacing(2)}
        top={theme.spacing(1)}
      >
        <IconButton
          onClick={onCancel}
          data-testid="VerificationDialog__Title--CloseButton"
        >
          <CloseIcon fill={theme.palette.grey["900"]} />
        </IconButton>
      </Box>
    </>
  );
};

const CallVerificationBody: React.FC<{
  contacts: AccountContact[];
}> = ({ contacts }) => {
  // Attached to the animated content panel to measure it's height
  const content = useRef<HTMLElement | null>(null);
  const [contentHeight, setContentHeight] = useState(0);

  const selection = useRef(0);
  const [isForward, setForwardDirection] = useState<boolean | null>(true);
  const [current, setCurrent] = useState<AccountContact | null>(() => {
    if (!contacts) {
      return null;
    }
    return contacts[0];
  });
  const formMethods = useForm();

  const onChange = useCallback(
    (id: string) => {
      const prevIndex = selection.current;
      const nextIndex = findIndex(contacts, { id });
      const isForward = prevIndex < nextIndex;

      // Update the selection history to determine direction
      selection.current = nextIndex;

      // Set new selection
      setCurrent(contacts[nextIndex]);
      setForwardDirection(isForward);
    },
    [selection, contacts, setCurrent],
  );

  const captureContentHeight = useCallback(() => {
    if (content.current) {
      const height = content.current.offsetHeight;
      setContentHeight(height);
    }
  }, [content, setContentHeight]);

  const variants = useMemo(
    () => ({
      enter: (isForward: boolean) => ({
        x: isForward ? "100%" : "-100%",
      }),
      center: {
        zIndex: 1,
        x: 0,
      },
      exit: (isForward: boolean) => ({
        zIndex: 0,
        x: isForward ? "-100%" : "100%",
      }),
    }),
    [],
  );

  return (
    <>
      <FormContext {...formMethods}>
        <ContactSelector contacts={contacts} onChange={onChange} />
      </FormContext>

      <Box mx={-4} overflow="hidden" height={contentHeight}>
        <AnimatePresence initial={false} custom={isForward}>
          {current && (
            <motion.div
              key={current.id}
              transition={{
                type: "spring",
                damping: 200,
                stiffness: 300,
              }}
              onAnimationStart={captureContentHeight}
              custom={isForward}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              <Box position="absolute" width={1} px={4}>
                <ContactPanel ref={content} contact={current} />
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </>
  );
};

export const CallVerificationDialog: React.FC<Props> = ({
  accountNumber,
  contacts,
  error,
  onCancel,
  onSkip,
  onConfirm,
  ...props
}) => {
  return (
    <CustomDialog onClose={onCancel} {...props}>
      <DialogTitle>
        <CallVerificationTitle
          accountNumber={accountNumber}
          onCancel={onCancel}
        />
        <Helmet>
          <title>{accountNumber} - Verify Customer</title>
        </Helmet>
      </DialogTitle>
      <CustomDialogContent dividers overflow="hidden">
        <Box clone mb={2}>
          <Typography variant="h5">Customer verification</Typography>
        </Box>
        <ErrorFallback error={error} title="Failed to load contacts">
          {contacts === null && (
            <Skeleton variant="rect" animation="wave" height={300} />
          )}
          {contacts !== null && <CallVerificationBody contacts={contacts} />}
        </ErrorFallback>
      </CustomDialogContent>
      <DialogActions>
        <Button
          disableElevation
          data-testid="VerificationDialog__Button--skip"
          onClick={onSkip}
          component="span"
          size="large"
          color="primary"
        >
          Skip
        </Button>
        <Button
          disableElevation
          data-testid="VerificationDialog__Button--confirm"
          autoFocus
          size="large"
          onClick={onConfirm}
          variant="contained"
          color="primary"
        >
          Confirm
        </Button>
      </DialogActions>
    </CustomDialog>
  );
};
